import { useEffect, useState } from 'react';
import { api, type PublishResult } from '../lib/api';
import { CloseIcon } from './icons';

type Diff = PublishResult['diff'];

function DiffGroup({ title, names }: { title: string; names: string[] }) {
  return (
    <div className="diff-group">
      <h4>{title} ({names.length})</h4>
      {names.length ? (
        <ul className="diff-list">
          {names.slice(0, 12).map((n) => <li key={n}>{n}</li>)}
          {names.length > 12 && (
            <li className="diff-empty">…and {names.length - 12} more</li>
          )}
        </ul>
      ) : (
        <p className="diff-empty">None</p>
      )}
    </div>
  );
}

/**
 * 发布确认。这里展示的 diff 是「预演」结果：先本地算出待发布内容与线上清单的差异，
 * 确认后才真正写快照。
 */
export function PublishDialog({
  preview, onClose, onDone,
}: {
  preview: { diff: Diff; trackCount: number; albumCount: number };
  onClose: () => void;
  onDone: (result: PublishResult) => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      onDone(await api.publish(note.trim()));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const noChange = !preview.diff.added.length
    && !preview.diff.removed.length
    && !preview.diff.changed.length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="close-round modal-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h3>Publish catalog</h3>
        <p className="sub">
          The app picks these changes up the next time it fetches the catalog.
          This release has {preview.albumCount} albums and {preview.trackCount} tracks.
        </p>

        {noChange ? (
          <p className="diff-empty">
            Nothing differs from the live catalog. Publishing only mints a new version number.
          </p>
        ) : (
          <>
            <DiffGroup title="Added" names={preview.diff.added} />
            <DiffGroup title="Changed" names={preview.diff.changed} />
            <DiffGroup title="Removed" names={preview.diff.removed} />
          </>
        )}

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="note">Note (optional)</label>
          <input
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. finished the third album"
          />
        </div>

        {error && <p className="error-text">{error}</p>}
        <div className="modal-actions">
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={confirm} disabled={busy}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function VersionHistory({
  onClose, onRolledBack,
}: {
  onClose: () => void;
  onRolledBack: (version: number) => void;
}) {
  const [versions, setVersions] = useState<Awaited<
    ReturnType<typeof api.listVersions>>['versions']>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.listVersions()
      .then((res) => setVersions(res.versions))
      .catch((err) => setError((err as Error).message));
  }, []);

  const rollback = async (version: number) => {
    setBusy(version);
    setError('');
    try {
      const res = await api.rollback(version);
      onRolledBack(res.version);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="close-round modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h3>Version history</h3>
        <p className="sub">
          Rolling back leaves the old version untouched — it republishes that content under a
          new version number, so version comparisons on the client stay monotonic.
        </p>
        {!versions.length && !error && <p className="diff-empty">Nothing published yet.</p>}
        {versions.map((v) => (
          <div className="version-row" key={v.version}>
            <span className="version-tag">v{v.version}</span>
            <div className="version-meta">
              {v.trackCount} tracks · {v.actor} · {new Date(v.publishedAt).toLocaleString('en-US')}
              {v.rolledBackFrom ? ` · rolled back from v${v.rolledBackFrom}` : ''}
              {v.note ? ` · ${v.note}` : ''}
            </div>
            <button
              className="tiny"
              disabled={busy !== null}
              onClick={() => rollback(v.version)}
            >
              {busy === v.version ? 'Rolling back…' : 'Roll back'}
            </button>
          </div>
        ))}
        {error && <p className="error-text">{error}</p>}
      </div>
    </div>
  );
}
